import { useCallback, useEffect, useRef, useState } from 'react';

import { useHistory, useLocation } from 'react-router';

import useQuery from '~/hooks/useQuery';

import { addQuery } from '~/utils/url';

export interface PromoTesterUrlState {
  orderId?: string;
  codeName?: string;
  applyWithOrignalUserAccount?: boolean;
  ignoreLuxPlusPricing?: boolean;
  logAfter?: Date;
  logBefore?: Date;
}

export interface UsePromoTesterUrlProps {
  brand: string;
}

export interface UsePromoTesterUrlReturn {
  urlState: PromoTesterUrlState;
  updateUrlState: (newState: Partial<PromoTesterUrlState>) => void;
}

export default function usePromoTesterUrl({ brand }: UsePromoTesterUrlProps): UsePromoTesterUrlReturn {
  const history = useHistory();
  const location = useLocation();
  const query = useQuery();
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Parse URL parameters
  const [urlState, setUrlState] = useState<PromoTesterUrlState>(() => ({
    orderId: query.get('orderId') || undefined,
    codeName: query.get('codeName') || undefined,
    applyWithOrignalUserAccount: query.get('applyWithOrignalUserAccount') === 'true',
    ignoreLuxPlusPricing: !query.get('ignoreLuxPlusPricing') || query.get('ignoreLuxPlusPricing') === 'true',
  }));

  // Debounced URL update function
  const debouncedUpdateUrl = useCallback(
    (updatedState: PromoTesterUrlState) => {
      // Clear existing timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Set new timeout for URL update
      debounceTimeoutRef.current = setTimeout(() => {
        // Remove undefined values before updating URL
        const urlParams = Object.fromEntries(Object.entries(updatedState).filter(([_, value]) => value !== undefined));
        const newLocation = addQuery(location, urlParams);
        history.push(newLocation);
      }, 300); // 300ms debounce delay
    },
    [location, history],
  );

  // Update URL and state
  const updateUrlState = useCallback(
    (newState: Partial<PromoTesterUrlState>) => {
      const updatedState = { ...urlState, ...newState };
      setUrlState(updatedState);

      // Debounce the URL update
      debouncedUpdateUrl(updatedState);
    },
    [urlState, debouncedUpdateUrl],
  );

  // Sync URL state when query parameters change
  useEffect(() => {
    const newUrlState: PromoTesterUrlState = {
      orderId: query.get('orderId') || undefined,
      codeName: query.get('codeName') || undefined,
      applyWithOrignalUserAccount: query.get('applyWithOrignalUserAccount') === 'true',
      ignoreLuxPlusPricing: !query.get('ignoreLuxPlusPricing') || query.get('ignoreLuxPlusPricing') === 'true',
      logAfter: query.get('logAfter') ? new Date(query.get('logAfter') || '') : undefined,
      logBefore: query.get('logBefore') ? new Date(query.get('logBefore') || '') : undefined,
    };

    // Only update state if there are actual changes to avoid unnecessary re-renders
    const hasChanges = JSON.stringify(newUrlState) !== JSON.stringify(urlState);

    if (hasChanges) {
      setUrlState(newUrlState);
    }
  }, [query, urlState]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return {
    urlState,
    updateUrlState,
  };
}
